/**
 * RoyETH v2 Carry-Trade Backtest Engine
 *
 * Models a vault that lever-loops DSV exposure:
 *   1. Deposit 1 wstETH as collateral on Aave         (earns supply APY)
 *   2. Borrow LTV_wstETH × P₀ USDC                    (accrues borrow interest)
 *   3. Deposit that USDC into DSV (srRoyUSDC)           (earns DSV senior yield)
 *   4. Borrow USDC against DSV at LTV_DSV, re-deposit   (lever loop)
 *      Effective DSV multiplier = 1 / (1 − LTV_DSV)
 *
 * Also computes an sroywstETH benchmark for comparison:
 *   sroywstETH = leveraged RoyETH v1 via external looping
 *   1. Deposit wstETH into RoyETH v1
 *   2. Borrow wstETH against minted royETH v1 tokens at sroywstETH_LTV
 *   3. Re-deposit borrowed wstETH into RoyETH v1 (lever loop)
 *   leverage = 1 / (1 − sroywstETH_LTV)
 *
 *   Balance sheet (ETH-normalized):
 *     Assets:  leverage × royethV1VsEth[t]
 *     Debt:    (leverage−1) × wstEthVsEth[t] × premiumCompound[t]
 *     NAV:     assets − debt
 *
 * Forked from royeth-engine.js.
 */

import { simulateRoyETH } from './royeth-engine';

const DEFAULTS = {
  aaveLTV: 39,              // % — LTV for wstETH → USDC borrow
  dsvLTV: 50,               // % — LTV for DSV → USDC lever loop
  useHistoricRates: true,
  stakingYieldOverride: 2.7,
  aaveSupplyOverride: 0.04,
  usdcBorrowOverride: 5.6,
  dsvBorrowOverride: 5.6,
  dsvBorrowSpread: 0,         // % — added on top of USDC borrow rate for DSV-collateralized borrows
  dsvBorrowAbsoluteRate: false, // if true, use dsvBorrowAbsolute instead of ubr + spread
  dsvBorrowAbsolute: 5.6,     // % — absolute borrow rate for DSV-collateralized USDC
  ltvMode: 'fixed',
  maxDsvRedemption: 3,
  harvestEnabled: true,
  harvestIntervalDays: 7,
  // sroywstETH benchmark params
  sroywstETHLTV: 50,             // % — LTV for borrowing wstETH against royETH v1
  sroywstETHBorrowPremium: 0.5,  // % — wstETH borrow premium over staking yield
  sroywstETHAbsoluteRate: false, // if true, use sroywstETHBorrowRate instead of staking + premium
  sroywstETHBorrowRate: 3.2,    // % — absolute wstETH borrow rate
  // RoyETH v1 config (passed through to simulateRoyETH)
  royethV1Config: null,
};

/**
 * @param {Object} opts
 * @param {Object} opts.dsvResult      – output of dsv-engine simulate()
 * @param {Object} opts.rateData       – { meta, data: { "YYYY-MM-DD": { ... } } }
 * @param {Object} [opts.config]       – overrides for DEFAULTS
 */
export function simulateRoyETHv2({ dsvResult, rateData, config: userConfig }) {
  const config = { ...DEFAULTS, ...userConfig };
  const ltvWstETH = config.aaveLTV / 100;
  const ltvDSV = config.dsvLTV / 100;
  const dsvMultiplier = ltvDSV < 1 ? 1 / (1 - ltvDSV) : 1;

  // sroywstETH benchmark params
  const sroywstETHLTV = config.sroywstETHLTV / 100;
  const sroywstETHLeverage = sroywstETHLTV < 1 ? 1 / (1 - sroywstETHLTV) : 1;
  const sroywstETHAbsoluteRate = config.sroywstETHAbsoluteRate;
  const sroywstETHAbsBorrowRate = config.sroywstETHBorrowRate / 100;
  const sroywstETHPremium = config.sroywstETHBorrowPremium / 100;

  const dates = dsvResult.dates;
  if (!dates || dates.length < 2) {
    return emptyResult();
  }

  // ── Compute RoyETH v1 result for sroywstETH benchmark ──
  const royethV1Result = simulateRoyETH({
    dsvResult,
    rateData,
    config: config.royethV1Config || {
      aaveLTV: config.aaveLTV,
      useHistoricRates: config.useHistoricRates,
      stakingYieldOverride: config.stakingYieldOverride,
      aaveSupplyOverride: config.aaveSupplyOverride,
      usdcBorrowOverride: config.usdcBorrowOverride,
      ltvMode: config.ltvMode,
      maxDsvRedemption: config.maxDsvRedemption,
      harvestEnabled: config.harvestEnabled,
      harvestIntervalDays: config.harvestIntervalDays,
    },
  });

  const dsvCum = dsvResult.aggregate.dsvCumReturn;
  const rateMap = (rateData && rateData.data) || {};

  // ── Initial prices ──
  const r0 = findRates(rateMap, dates[0], config);
  const P0 = r0.wstEthPrice;
  const ethPrice0 = r0.ethPrice;
  const initRatio = ethPrice0 > 0 ? P0 / ethPrice0 : 1.22;

  if (config.ltvMode === 'dynamic') {
    return simulateDynamic({ dates, dsvCum, rateMap, config, ltvWstETH, ltvDSV, dsvMultiplier, sroywstETHLeverage, sroywstETHPremium, sroywstETHAbsoluteRate, sroywstETHAbsBorrowRate, royethV1Result, P0, ethPrice0, initRatio });
  }

  // ── Fixed LTV mode ──

  let stakeCompound = 1.0;
  let supplyCompound = 1.0;
  let borrowCompoundWstETH = 1.0;
  let borrowCompoundDSV = 1.0;

  // ── Harvest state ──
  const harvestEnabled = config.harvestEnabled;
  const harvestInterval = config.harvestIntervalDays || 7;
  let collateralUnits = 1.0;
  const initialBorrow = ltvWstETH * P0;
  let debtWstETH = initialBorrow;
  let debtDSV = initialBorrow * ltvDSV / (1 - ltvDSV);
  let dsvUSD = initialBorrow * dsvMultiplier;
  let daysSinceHarvest = 0;
  const harvests = [];

  // ── sroywstETH benchmark accumulator ──
  // premiumCompound tracks only the borrow premium portion (staking appreciation
  // is already captured by wstEthVsEth in the balance sheet formula)
  let premiumCompound = 1.0;

  // ── Output series ──
  const royethV2Nav = [1.0];
  const ethIndexed = [1.0];
  const wstEthIndexed = [1.0];
  const dsvSrIndexed = [1.0];
  const dailyApy = [0];
  const sroywstETHDailyApy = [0];
  const components = { staking: [0], supply: [0], carry: [0], leveragedDsvIncome: [0], wstETHDebtCost: [0], dsvDebtCost: [0], dsvYield: [0], borrowRate: [0], dsvBorrowRate: [0] };
  const sroywstETHComponents = { royethV1Apy: [0], borrowCost: [0], v1Staking: [0], v1Supply: [0], v1Carry: [0], v1DsvYield: [0], v1BorrowRate: [0] };
  const rawRates = [null]; // per-day raw rate snapshot for the detail panel
  const royethV2VsEth = [1.0];
  const sroywstETHVsEth = [1.0];
  const wstEthVsEth = [1.0];
  const effectiveLeverage = [dsvMultiplier];

  for (let i = 1; i < dates.length; i++) {
    const calDays = (Date.parse(dates[i]) - Date.parse(dates[i - 1])) / 86400000;
    const dt = calDays > 0 ? calDays : 1;

    const rates = findRates(rateMap, dates[i], config);
    const asup = (config.useHistoricRates ? rates.aaveSupplyYield : config.aaveSupplyOverride) / 100;
    const ubr = (config.useHistoricRates ? rates.usdcBorrowRate : config.usdcBorrowOverride) / 100;
    const dbr = config.dsvBorrowAbsoluteRate
      ? config.dsvBorrowAbsolute / 100
      : ((config.useHistoricRates ? rates.usdcBorrowRate : config.dsvBorrowOverride) + config.dsvBorrowSpread) / 100;
    const sk = (config.useHistoricRates ? rates.stakingYield : config.stakingYieldOverride) / 100;

    const stakingDaily = Math.pow(1 + sk, dt / 365) - 1;
    stakeCompound *= (1 + stakingDaily);

    const supplyDaily = Math.pow(1 + asup, dt / 365) - 1;
    supplyCompound *= (1 + supplyDaily);

    const borrowDailyWstETH = Math.pow(1 + ubr, dt / 365) - 1;
    const borrowDailyDSV = Math.pow(1 + dbr, dt / 365) - 1;
    borrowCompoundWstETH *= (1 + borrowDailyWstETH);
    borrowCompoundDSV *= (1 + borrowDailyDSV);

    // ── sroywstETH benchmark ──
    // NAV(t) = leverage × royethV1VsEth[t] − (leverage−1) × wstEthVsEth[t] × premiumCompound[t]
    // In absolute mode, effective premium = absoluteRate − stakingYield (can be per-day)
    const effPremium = sroywstETHAbsoluteRate ? Math.max(sroywstETHAbsBorrowRate - sk, 0) : sroywstETHPremium;
    const premiumDaily = Math.pow(1 + effPremium, dt / 365) - 1;
    premiumCompound *= (1 + premiumDaily);
    const sroywstETHNav = sroywstETHLeverage * royethV1Result.royethVsEth[i]
      - (sroywstETHLeverage - 1) * royethV1Result.wstEthVsEth[i] * premiumCompound;
    sroywstETHVsEth.push(sroywstETHNav);

    if (harvestEnabled) {
      debtWstETH *= (1 + borrowDailyWstETH);
      debtDSV *= (1 + borrowDailyDSV);
      const dsvPrevVal = 1 + dsvCum[i - 1];
      const dsvDailyRet = dsvPrevVal > 0 ? (1 + dsvCum[i]) / dsvPrevVal - 1 : 0;
      dsvUSD *= (1 + dsvDailyRet);
      daysSinceHarvest += dt;

      if (daysSinceHarvest >= harvestInterval) {
        const totalDebt = debtWstETH + debtDSV;
        const excessUSD = dsvUSD - totalDebt;
        if (excessUSD > 0) {
          const wstEthPrice = (rates.wstEthPrice > 0) ? rates.wstEthPrice : P0;
          const wstEthBought = excessUSD / wstEthPrice;
          const newBorrowWstETH = excessUSD * ltvWstETH;
          const newBorrowDSV = newBorrowWstETH * ltvDSV / (1 - ltvDSV);
          collateralUnits += wstEthBought;
          debtWstETH += newBorrowWstETH;
          debtDSV += newBorrowDSV;
          dsvUSD = debtWstETH + debtDSV;
          harvests.push({ date: dates[i], excessUSD, wstEthBought, newBorrowWstETH });
        }
        daysSinceHarvest = 0;
      }
    }

    const ethIdx = (ethPrice0 > 0 && rates.ethPrice > 0)
      ? rates.ethPrice / ethPrice0
      : ethIndexed[i - 1];

    const wstIdx = (P0 > 0 && rates.wstEthPrice > 0)
      ? rates.wstEthPrice / P0
      : wstEthIndexed[i - 1];

    const dsvValue = 1 + dsvCum[i];

    // ── NAV ──
    let nav;
    if (harvestEnabled) {
      const wstEthPrice = (rates.wstEthPrice > 0) ? rates.wstEthPrice : P0;
      const collateralValueUSD = collateralUnits * wstEthPrice * supplyCompound;
      const totalDebt = debtWstETH + debtDSV;
      const navUSD = collateralValueUSD + dsvUSD - totalDebt;
      nav = navUSD / P0;
    } else {
      const totalDebtFactor = ltvWstETH * dsvMultiplier;
      nav = wstIdx * supplyCompound
        + totalDebtFactor * dsvValue
        - ltvWstETH * borrowCompoundWstETH
        - (ltvWstETH * ltvDSV / (1 - ltvDSV)) * borrowCompoundDSV;
    }
    royethV2Nav.push(nav);
    ethIndexed.push(ethIdx);
    wstEthIndexed.push(wstIdx);
    dsvSrIndexed.push(dsvValue);

    // ── ETH-normalized ──
    wstEthVsEth.push(stakeCompound);

    if (harvestEnabled) {
      const wstEthPrice = (rates.wstEthPrice > 0) ? rates.wstEthPrice : P0;
      const collateralValueUSD = collateralUnits * wstEthPrice * supplyCompound;
      const totalDebt = debtWstETH + debtDSV;
      const navUSD = collateralValueUSD + dsvUSD - totalDebt;
      const ethPrice = (rates.ethPrice > 0) ? rates.ethPrice : ethPrice0;
      const initNavEth = P0 / ethPrice0;
      royethV2VsEth.push(initNavEth > 0 ? (navUSD / ethPrice) / initNavEth : 1.0);
    } else {
      const totalDebtFactor = ltvWstETH * dsvMultiplier;
      const carryInEth = ethIdx > 0
        ? totalDebtFactor * (dsvValue - borrowCompoundWstETH) / ethIdx
        : 0;
      const dsvDebtCostInEth = ethIdx > 0
        ? (ltvWstETH * ltvDSV / (1 - ltvDSV)) * (borrowCompoundDSV - borrowCompoundWstETH) / ethIdx
        : 0;
      royethV2VsEth.push(stakeCompound * supplyCompound + carryInEth * initRatio - dsvDebtCostInEth * initRatio);
    }

    effectiveLeverage.push(harvestEnabled && debtWstETH > 0 ? dsvUSD / debtWstETH : dsvMultiplier);

    // ── Instantaneous APY ──
    const dsvPrev = 1 + dsvCum[i - 1];
    const dsvDailyRet = dsvPrev > 0 ? (1 + dsvCum[i]) / dsvPrev - 1 : 0;
    const srYieldAnn = (Math.pow(1 + dsvDailyRet, 365 / dt) - 1) * 100;
    const stakingComp = sk * 100;
    const supplyComp = asup * 100;
    const wstETHBorrowCost = ltvWstETH * ubr * 100;
    const dsvBorrowCost = (ltvWstETH * ltvDSV / (1 - ltvDSV)) * dbr * 100;
    const leveragedDsvYield = ltvWstETH * dsvMultiplier * srYieldAnn;
    const carryComp = leveragedDsvYield - wstETHBorrowCost - dsvBorrowCost;

    dailyApy.push(stakingComp + supplyComp + carryComp);
    components.staking.push(stakingComp);
    components.supply.push(supplyComp);
    components.carry.push(carryComp);
    components.leveragedDsvIncome.push(leveragedDsvYield);
    components.wstETHDebtCost.push(wstETHBorrowCost);
    components.dsvDebtCost.push(dsvBorrowCost);
    components.dsvYield.push(srYieldAnn);
    components.borrowRate.push(ubr * 100);
    components.dsvBorrowRate.push(dbr * 100);

    // sroywstETH APY: leverage × v1APY − (leverage−1) × wstETHBorrowRate
    const v1Apy = royethV1Result.dailyApy[i] || 0;
    const sroywstETHGross = sroywstETHLeverage * v1Apy;
    const wstETHBorrowRateAnn = sroywstETHAbsoluteRate ? sroywstETHAbsBorrowRate * 100 : (sk + sroywstETHPremium) * 100;
    const sroywstETHBorrowCostAnn = (sroywstETHLeverage - 1) * wstETHBorrowRateAnn;
    const sroywstETHApyVal = sroywstETHGross - sroywstETHBorrowCostAnn;
    sroywstETHDailyApy.push(sroywstETHApyVal);
    sroywstETHComponents.royethV1Apy.push(v1Apy);
    sroywstETHComponents.borrowCost.push(sroywstETHBorrowCostAnn);
    sroywstETHComponents.v1Staking.push(royethV1Result.components.staking[i] || 0);
    sroywstETHComponents.v1Supply.push(royethV1Result.components.supply[i] || 0);
    sroywstETHComponents.v1Carry.push(royethV1Result.components.carry[i] || 0);
    sroywstETHComponents.v1DsvYield.push(royethV1Result.components.dsvYield[i] || 0);
    sroywstETHComponents.v1BorrowRate.push(royethV1Result.components.borrowRate[i] || 0);

    // Raw rates for detail panel
    rawRates.push({
      stakingYield: sk * 100,
      aaveSupply: asup * 100,
      usdcBorrow: ubr * 100,
      dsvBorrow: dbr * 100,
      dsvYield: srYieldAnn,
      wstEthBorrowRate: wstETHBorrowRateAnn,
      wstEthBorrowPremium: sroywstETHAbsoluteRate ? (sroywstETHAbsBorrowRate - sk) * 100 : sroywstETHPremium * 100,
    });
  }

  return buildSummary({ dates, royethV2Nav, royethV2VsEth, sroywstETHVsEth, wstEthVsEth, ethIndexed, wstEthIndexed, dsvSrIndexed, dailyApy, sroywstETHDailyApy, components, sroywstETHComponents, rawRates, actualLTV: null, effectiveLeverage, harvests, dsvMultiplier, sroywstETHLeverage });
}

/**
 * Dynamic LTV simulation — tracks explicit USD amounts and rebalances daily.
 */
function simulateDynamic({ dates, dsvCum, rateMap, config, ltvWstETH, ltvDSV, dsvMultiplier, sroywstETHLeverage, sroywstETHPremium, sroywstETHAbsoluteRate, sroywstETHAbsBorrowRate, royethV1Result, P0, ethPrice0, initRatio }) {
  const maxRedeemPct = config.maxDsvRedemption / 100;

  let collateralUnits = 1.0;
  const initialBorrow = ltvWstETH * P0;
  let debtWstETH = initialBorrow;
  let debtDSV = initialBorrow * ltvDSV / (1 - ltvDSV);
  let dsvUSD = initialBorrow * dsvMultiplier;

  const harvestEnabled = config.harvestEnabled;
  const harvestInterval = config.harvestIntervalDays || 7;
  let daysSinceHarvest = 0;
  const harvests = [];

  let stakeCompound = 1.0;
  let supplyCompound = 1.0;
  let premiumCompound = 1.0;

  const royethV2Nav = [1.0];
  const ethIndexed = [1.0];
  const wstEthIndexed = [1.0];
  const dsvSrIndexed = [1.0];
  const dailyApy = [0];
  const sroywstETHDailyApy = [0];
  const components = { staking: [0], supply: [0], carry: [0], leveragedDsvIncome: [0], wstETHDebtCost: [0], dsvDebtCost: [0], dsvYield: [0], borrowRate: [0], dsvBorrowRate: [0] };
  const sroywstETHComponents = { royethV1Apy: [0], borrowCost: [0], v1Staking: [0], v1Supply: [0], v1Carry: [0], v1DsvYield: [0], v1BorrowRate: [0] };
  const rawRates = [null];
  const royethV2VsEth = [1.0];
  const sroywstETHVsEth = [1.0];
  const wstEthVsEth = [1.0];
  const actualLTV = [ltvWstETH * 100];
  const effectiveLeverage = [dsvMultiplier];

  for (let i = 1; i < dates.length; i++) {
    const calDays = (Date.parse(dates[i]) - Date.parse(dates[i - 1])) / 86400000;
    const dt = calDays > 0 ? calDays : 1;

    const rates = findRates(rateMap, dates[i], config);
    const asup = (config.useHistoricRates ? rates.aaveSupplyYield : config.aaveSupplyOverride) / 100;
    const ubr = (config.useHistoricRates ? rates.usdcBorrowRate : config.usdcBorrowOverride) / 100;
    const dbr = config.dsvBorrowAbsoluteRate
      ? config.dsvBorrowAbsolute / 100
      : ((config.useHistoricRates ? rates.usdcBorrowRate : config.dsvBorrowOverride) + config.dsvBorrowSpread) / 100;
    const sk = (config.useHistoricRates ? rates.stakingYield : config.stakingYieldOverride) / 100;

    const stakingDaily = Math.pow(1 + sk, dt / 365) - 1;
    stakeCompound *= (1 + stakingDaily);

    const supplyDaily = Math.pow(1 + asup, dt / 365) - 1;
    supplyCompound *= (1 + supplyDaily);

    // ── sroywstETH benchmark ──
    const effPremium = sroywstETHAbsoluteRate ? Math.max(sroywstETHAbsBorrowRate - sk, 0) : sroywstETHPremium;
    const premiumDaily = Math.pow(1 + effPremium, dt / 365) - 1;
    premiumCompound *= (1 + premiumDaily);
    const sroywstETHNav = sroywstETHLeverage * royethV1Result.royethVsEth[i]
      - (sroywstETHLeverage - 1) * royethV1Result.wstEthVsEth[i] * premiumCompound;
    sroywstETHVsEth.push(sroywstETHNav);

    // Accrue interest
    const borrowDailyWstETH = Math.pow(1 + ubr, dt / 365) - 1;
    const borrowDailyDSV = Math.pow(1 + dbr, dt / 365) - 1;
    debtWstETH *= (1 + borrowDailyWstETH);
    debtDSV *= (1 + borrowDailyDSV);

    // DSV yield
    const dsvPrev = 1 + dsvCum[i - 1];
    const dsvDailyRet = dsvPrev > 0 ? (1 + dsvCum[i]) / dsvPrev - 1 : 0;
    dsvUSD *= (1 + dsvDailyRet);

    const ethPrice = (rates.ethPrice > 0) ? rates.ethPrice : ethPrice0;

    // ── Harvest ──
    if (harvestEnabled) {
      daysSinceHarvest += dt;
      if (daysSinceHarvest >= harvestInterval) {
        const totalDebt = debtWstETH + debtDSV;
        const excessUSD = dsvUSD - totalDebt;
        if (excessUSD > 0) {
          const wstEthPriceForHarvest = ethPrice * stakeCompound * initRatio;
          const wstEthBought = excessUSD / wstEthPriceForHarvest;
          const newBorrowWstETH = excessUSD * ltvWstETH;
          const newBorrowDSV = newBorrowWstETH * ltvDSV / (1 - ltvDSV);
          collateralUnits += wstEthBought;
          debtWstETH += newBorrowWstETH;
          debtDSV += newBorrowDSV;
          dsvUSD = debtWstETH + debtDSV;
          harvests.push({ date: dates[i], excessUSD, wstEthBought, newBorrowWstETH });
        }
        daysSinceHarvest = 0;
      }
    }

    // Collateral value
    const wstEthPriceDerived = ethPrice * stakeCompound * initRatio;
    const collateralUSD = collateralUnits * wstEthPriceDerived * supplyCompound;
    const currentLTV = collateralUSD > 0 ? debtWstETH / collateralUSD : 0;

    // Rebalance wstETH LTV
    const desiredWstETHDebt = ltvWstETH * collateralUSD;

    if (currentLTV < ltvWstETH) {
      const borrowMore = desiredWstETHDebt - debtWstETH;
      const dsvLoopDeposit = borrowMore * dsvMultiplier;
      const dsvLoopDebt = borrowMore * ltvDSV / (1 - ltvDSV);
      debtWstETH += borrowMore;
      debtDSV += dsvLoopDebt;
      dsvUSD += dsvLoopDeposit;
    } else if (currentLTV > ltvWstETH) {
      const repayAmt = debtWstETH - desiredWstETHDebt;
      const maxRedeem = maxRedeemPct * dsvUSD;
      const actualRepay = Math.min(repayAmt, maxRedeem);
      const totalDebtNow = debtWstETH + debtDSV;
      const wstETHShare = totalDebtNow > 0 ? debtWstETH / totalDebtNow : 0.5;
      dsvUSD -= actualRepay;
      debtWstETH -= actualRepay * wstETHShare;
      debtDSV -= actualRepay * (1 - wstETHShare);
    }

    const ltvAfter = collateralUSD > 0 ? (debtWstETH / collateralUSD) * 100 : 0;
    actualLTV.push(ltvAfter);

    effectiveLeverage.push(debtWstETH > 0 ? dsvUSD / debtWstETH : dsvMultiplier);

    // NAV
    const navUSD = collateralUSD + dsvUSD - (debtWstETH + debtDSV);

    const ethIdx = (ethPrice0 > 0 && rates.ethPrice > 0)
      ? rates.ethPrice / ethPrice0
      : ethIndexed[i - 1];

    royethV2Nav.push(navUSD / P0);
    ethIndexed.push(ethIdx);
    wstEthIndexed.push(wstEthPriceDerived / P0);
    dsvSrIndexed.push(1 + dsvCum[i]);

    wstEthVsEth.push(stakeCompound);
    const initNavEth = P0 / ethPrice0;
    royethV2VsEth.push(initNavEth > 0 ? (navUSD / ethPrice) / initNavEth : 1.0);

    // Instantaneous APY
    const srYieldAnn = (Math.pow(1 + dsvDailyRet, 365 / dt) - 1) * 100;
    const effLTV = collateralUSD > 0 ? debtWstETH / collateralUSD : ltvWstETH;
    const effLeverage = debtWstETH > 0 ? dsvUSD / debtWstETH : dsvMultiplier;
    const stakingComp = sk * 100;
    const supplyComp = asup * 100;
    const leveragedDsvYield = effLTV * effLeverage * srYieldAnn;
    const wstETHBorrowCost = effLTV * ubr * 100;
    const dsvBorrowCost = effLTV * (effLeverage - 1) * dbr * 100;
    const carryComp = leveragedDsvYield - wstETHBorrowCost - dsvBorrowCost;

    dailyApy.push(stakingComp + supplyComp + carryComp);
    components.staking.push(stakingComp);
    components.supply.push(supplyComp);
    components.carry.push(carryComp);
    components.leveragedDsvIncome.push(leveragedDsvYield);
    components.wstETHDebtCost.push(wstETHBorrowCost);
    components.dsvDebtCost.push(dsvBorrowCost);
    components.dsvYield.push(srYieldAnn);
    components.borrowRate.push(ubr * 100);
    components.dsvBorrowRate.push(dbr * 100);

    const v1Apy = royethV1Result.dailyApy[i] || 0;
    const sroywstETHGross = sroywstETHLeverage * v1Apy;
    const wstETHBorrowRateAnn = sroywstETHAbsoluteRate ? sroywstETHAbsBorrowRate * 100 : (sk + sroywstETHPremium) * 100;
    const sroywstETHBorrowCostAnn = (sroywstETHLeverage - 1) * wstETHBorrowRateAnn;
    const sroywstETHApyVal = sroywstETHGross - sroywstETHBorrowCostAnn;
    sroywstETHDailyApy.push(sroywstETHApyVal);
    sroywstETHComponents.royethV1Apy.push(v1Apy);
    sroywstETHComponents.borrowCost.push(sroywstETHBorrowCostAnn);
    sroywstETHComponents.v1Staking.push(royethV1Result.components.staking[i] || 0);
    sroywstETHComponents.v1Supply.push(royethV1Result.components.supply[i] || 0);
    sroywstETHComponents.v1Carry.push(royethV1Result.components.carry[i] || 0);
    sroywstETHComponents.v1DsvYield.push(royethV1Result.components.dsvYield[i] || 0);
    sroywstETHComponents.v1BorrowRate.push(royethV1Result.components.borrowRate[i] || 0);

    rawRates.push({
      stakingYield: sk * 100,
      aaveSupply: asup * 100,
      usdcBorrow: ubr * 100,
      dsvBorrow: dbr * 100,
      dsvYield: srYieldAnn,
      wstEthBorrowRate: wstETHBorrowRateAnn,
      wstEthBorrowPremium: sroywstETHAbsoluteRate ? (sroywstETHAbsBorrowRate - sk) * 100 : sroywstETHPremium * 100,
    });
  }

  return buildSummary({ dates, royethV2Nav, royethV2VsEth, sroywstETHVsEth, wstEthVsEth, ethIndexed, wstEthIndexed, dsvSrIndexed, dailyApy, sroywstETHDailyApy, components, sroywstETHComponents, rawRates, actualLTV, effectiveLeverage, harvests, dsvMultiplier, sroywstETHLeverage });
}

// ── Build summary from output series ──

function buildSummary({ dates, royethV2Nav, royethV2VsEth, sroywstETHVsEth, wstEthVsEth, ethIndexed, wstEthIndexed, dsvSrIndexed, dailyApy, sroywstETHDailyApy, components, sroywstETHComponents, rawRates, actualLTV, effectiveLeverage, harvests, dsvMultiplier, sroywstETHLeverage }) {
  const totalDays = (Date.parse(dates[dates.length - 1]) - Date.parse(dates[0])) / 86400000;
  const lastVsEth = royethV2VsEth[royethV2VsEth.length - 1];
  const totalReturn = lastVsEth - 1;
  const annualizedReturn = totalDays > 0
    ? Math.pow(lastVsEth, 365 / totalDays) - 1
    : 0;
  const n = Math.max(dailyApy.length - 1, 1);
  const avgApy = dailyApy.slice(1).reduce((s, v) => s + v, 0) / n;
  const avgDsvYield = components.dsvYield.slice(1).reduce((s, v) => s + v, 0) / n;
  const avgBorrowRate = components.borrowRate.slice(1).reduce((s, v) => s + v, 0) / n;

  const sroywstETHReturn = sroywstETHVsEth[sroywstETHVsEth.length - 1] - 1;
  const vsSroywstETHTotal = totalReturn - sroywstETHReturn;
  const lastVsSroywstETH = lastVsEth / sroywstETHVsEth[sroywstETHVsEth.length - 1];
  const vsSroywstETHAnnualized = totalDays > 0
    ? Math.pow(lastVsSroywstETH, 365 / totalDays) - 1
    : 0;

  return {
    dates,
    royethV2Nav,
    royethV2VsEth,
    sroywstETHVsEth,
    wstEthVsEth,
    ethIndexed,
    wstEthIndexed,
    dsvSrIndexed,
    dailyApy,
    sroywstETHDailyApy,
    components,
    sroywstETHComponents,
    rawRates,
    actualLTV,
    effectiveLeverage,
    harvests,
    sroywstETHLeverage,
    summary: {
      totalReturn,
      annualizedReturn,
      days: totalDays,
      avgApy,
      avgDsvYield,
      avgBorrowRate,
      sroywstETHReturn,
      vsSroywstETH: vsSroywstETHTotal,
      vsSroywstETHAnnualized,
      dsvMultiplier,
    },
  };
}

// ── Helpers ──

function findRates(rateMap, date, config) {
  const entry = rateMap[date];
  if (entry) return entry;

  const d = new Date(date + "T00:00:00Z");
  for (let k = 1; k <= 7; k++) {
    d.setUTCDate(d.getUTCDate() - 1);
    const key = d.toISOString().slice(0, 10);
    if (rateMap[key]) return rateMap[key];
  }

  return {
    stakingYield: config.stakingYieldOverride,
    aaveSupplyYield: config.aaveSupplyOverride,
    usdcBorrowRate: config.usdcBorrowOverride,
    ethPrice: 0,
    wstEthPrice: 0,
  };
}

function emptyResult() {
  return {
    dates: [],
    royethV2Nav: [],
    royethV2VsEth: [],
    sroywstETHVsEth: [],
    wstEthVsEth: [],
    ethIndexed: [],
    wstEthIndexed: [],
    dsvSrIndexed: [],
    dailyApy: [],
    sroywstETHDailyApy: [],
    components: { staking: [], supply: [], carry: [], leveragedDsvIncome: [], wstETHDebtCost: [], dsvDebtCost: [], dsvYield: [], borrowRate: [], dsvBorrowRate: [] },
    sroywstETHComponents: { royethV1Apy: [], borrowCost: [], v1Staking: [], v1Supply: [], v1Carry: [], v1DsvYield: [], v1BorrowRate: [] },
    rawRates: [],
    actualLTV: null,
    effectiveLeverage: [],
    harvests: [],
    sroywstETHLeverage: 1,
    summary: {
      totalReturn: 0, annualizedReturn: 0, days: 0, avgApy: 0,
      avgDsvYield: 0, avgBorrowRate: 0,
      sroywstETHReturn: 0, vsSroywstETH: 0, vsSroywstETHAnnualized: 0,
      dsvMultiplier: 1,
    },
  };
}
